import { supabase } from "./supabase";

export function whatsappUrl(number, message) {
  const digits = number.replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export async function sendNotificationEmail(to, subject, html, replyTo, replyName, fromAddress, fromName) {
  console.log("[sendNotificationEmail] to:", to, "subject:", subject);
  if (!to) {
    console.warn("[sendNotificationEmail] No recipient email provided");
    return;
  }

  const payload = { to, subject, html, replyTo, replyName, fromAddress, fromName };

  const response = await fetch("/api/email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("[sendNotificationEmail] Server error:", response.status, errorText);
  }
}

// ---- LEADS ----
export async function createLead({ name, email, phone, service, message, contactMethod }) {
  const res = await fetch("/api/lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, phone, service, message, contactMethod }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Erro ao enviar pedido");
  }
  return res.json();
}

export async function getLeads() {
  const { data, error } = await supabase
    .from("emilly_leads")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

export async function updateLeadStatus(id, status) {
  const { data, error } = await supabase
    .from("emilly_leads")
    .update({ status })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteLead(id) {
  const { error } = await supabase.from("emilly_leads").delete().eq("id", id);
  if (error) throw error;
}

// ---- TESTIMONIALS ----
export async function getPublishedTestimonials() {
  const { data, error } = await supabase
    .from("emilly_testimonials")
    .select("*")
    .eq("published", true)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

export async function getAllTestimonials() {
  const { data, error } = await supabase
    .from("emilly_testimonials")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

export async function createTestimonial(form) {
  const { data, error } = await supabase
    .from("emilly_testimonials")
    .insert(form)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateTestimonial(id, form) {
  const { data, error } = await supabase
    .from("emilly_testimonials")
    .update(form)
    .eq("id", id)
    .select()
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Depoimento não encontrado");
  return data;
}

export async function deleteTestimonial(id) {
  const { error } = await supabase.from("emilly_testimonials").delete().eq("id", id);
  if (error) throw error;
}

// ---- PORTFOLIO ----
export async function getPortfolio() {
  const { data, error } = await supabase
    .from("emilly_portfolio")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

export async function createPortfolioItem(form) {
  const { data, error } = await supabase
    .from("emilly_portfolio")
    .insert(form)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updatePortfolioItem(id, form) {
  const { data, error } = await supabase
    .from("emilly_portfolio")
    .update(form)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deletePortfolioItem(id) {
  const { error } = await supabase.from("emilly_portfolio").delete().eq("id", id);
  if (error) throw error;
}

// ---- CONTACT METHODS ----
export async function getContactMethods() {
  const { data, error } = await supabase
    .from("emilly_contact_methods")
    .select("*")
    .order("order_index", { ascending: true });
  if (error) {
    // Table may not exist yet — return empty
    console.warn("[API] getContactMethods:", error.message);
    return [];
  }
  return (data || []).map(r => r.name);
}

export async function updateContactMethods(names) {
  const { error: delError } = await supabase
    .from("emilly_contact_methods")
    .delete()
    .neq("id", "00000000-0000-0000-0000-000000000000");
  if (delError) throw delError;

  const { data, error } = await supabase
    .from("emilly_contact_methods")
    .insert(names.map((name, i) => ({ name, order_index: i })))
    .select();
  if (error) throw error;
  return data;
}

// ---- SETTINGS (solo variables no-ui) ----
export async function getSettings() {
  const { data, error } = await supabase.from("emilly_settings").select("*");
  if (error) throw error;
  return data.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {});
}

export async function updateSetting(key, value) {
  const { data, error } = await supabase
    .from("emilly_settings")
    .upsert({ key, value }, { onConflict: 'key' })
    .select();
  if (error) throw error;
  return data[0];
}

// ---- EMAIL PROCESSES ----
export async function getEmailProcesses() {
  const { data, error } = await supabase
    .from("emilly_email_processes")
    .select("*, emilly_email_recipients(*)")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(p => ({
    ...p,
    recipients: p.emilly_email_recipients || [],
  }));
}

export async function getEmailProcess(key) {
  const { data, error } = await supabase
    .from("emilly_email_processes")
    .select("*, emilly_email_recipients(*)")
    .eq("process_key", key)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { ...data, recipients: data.emilly_email_recipients || [] };
}

export async function updateEmailProcess(id, settings) {
  const { data, error } = await supabase
    .from("emilly_email_processes")
    .update(settings)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function createEmailProcess({ process_key, label, enabled, email_subject, email_from_name, email_from_address, email_template }) {
  const { data, error } = await supabase
    .from("emilly_email_processes")
    .insert({ process_key, label, enabled, email_subject, email_from_name, email_from_address, email_template })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteEmailProcess(id) {
  const { error } = await supabase
    .from("emilly_email_processes")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

// ---- EMAIL RECIPIENTS ----
export async function getEmailRecipients(processId) {
  const { data, error } = await supabase
    .from("emilly_email_recipients")
    .select("*")
    .eq("process_id", processId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function addEmailRecipient({ process_id, email, label, enabled }) {
  const { data, error } = await supabase
    .from("emilly_email_recipients")
    .insert({ process_id, email, label: label || '', enabled: enabled !== false })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateEmailRecipient(id, { email, label, enabled }) {
  const { data, error } = await supabase
    .from("emilly_email_recipients")
    .update({ email, label, enabled })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteEmailRecipient(id) {
  const { error } = await supabase
    .from("emilly_email_recipients")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

// ---- SERVICES ----
export async function getServices() {
  const { data, error } = await supabase
    .from("emilly_services")
    .select("*")
    .order("order_index", { ascending: true });
  if (error) throw error;
  return data;
}

export async function createService(form) {
  const { data, error } = await supabase
    .from("emilly_services")
    .insert(form)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateService(id, updates) {
  const { data, error } = await supabase
    .from("emilly_services")
    .update(updates)
    .eq("id", id)
    .select();
  if (error) throw error;
  return data[0];
}

export async function deleteService(id) {
  const { error } = await supabase.from("emilly_services").delete().eq("id", id);
  if (error) throw error;
}

// ---- HERO (fila unica) ----
export async function getHero() {
  const { data, error } = await supabase
    .from("emilly_hero")
    .select("*")
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateHero(content) {
  const { data: existing } = await supabase
    .from("emilly_hero")
    .select("id")
    .limit(1)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from("emilly_hero")
      .update(content)
      .eq("id", existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from("emilly_hero")
    .insert(content)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ---- ABOUT (fila unica) ----
export async function getAbout() {
  const { data, error } = await supabase
    .from("emilly_about")
    .select("*")
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateAbout(content) {
  const { data: existing } = await supabase
    .from("emilly_about")
    .select("id")
    .limit(1)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from("emilly_about")
      .update(content)
      .eq("id", existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from("emilly_about")
    .insert(content)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ---- GLOBALS (fila unica) ----
export async function getGlobals() {
  const { data, error } = await supabase
    .from("emilly_globals")
    .select("*")
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateGlobals(content) {
  const { data: existing } = await supabase
    .from("emilly_globals")
    .select("id")
    .limit(1)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from("emilly_globals")
      .update(content)
      .eq("id", existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from("emilly_globals")
    .insert(content)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ---- SECTION HEADERS ----
export async function getSectionHeaders() {
  const { data, error } = await supabase
    .from("emilly_section_headers")
    .select("*");
  if (error) throw error;
  // Return as object keyed by section
  const map = {};
  (data || []).forEach(row => { map[row.section] = { eyebrow: row.eyebrow, headline: row.headline, description: row.description }; });
  return map;
}

export async function updateSectionHeader(section, content) {
  const { data, error } = await supabase
    .from("emilly_section_headers")
    .upsert({ section, ...content }, { onConflict: 'section' })
    .select();
  if (error) throw error;
  return data[0];
}

// ---- NAVIGATION ----
export async function getNavigation() {
  const { data, error } = await supabase
    .from("emilly_navigation")
    .select("*")
    .order("order_index", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function updateNavigation(items) {
  // Delete all, re-insert
  const { error: delError } = await supabase
    .from("emilly_navigation")
    .delete()
    .neq("id", "00000000-0000-0000-0000-000000000000");
  if (delError) throw delError;

  const { data, error } = await supabase
    .from("emilly_navigation")
    .insert(items.map((item, i) => ({ label: item.label, href: item.href, order_index: i })))
    .select();
  if (error) throw error;
  return data;
}

// ---- FORM SERVICES ----
export async function getFormServices() {
  const { data, error } = await supabase
    .from("emilly_form_services")
    .select("*")
    .order("order_index", { ascending: true });
  if (error) throw error;
  return (data || []).map(r => r.name);
}

export async function updateFormServices(names) {
  const { error: delError } = await supabase
    .from("emilly_form_services")
    .delete()
    .neq("id", "00000000-0000-0000-0000-000000000000");
  if (delError) throw delError;

  const { data, error } = await supabase
    .from("emilly_form_services")
    .insert(names.map((name, i) => ({ name, order_index: i })))
    .select();
  if (error) throw error;
  return data;
}

// ---- UI STRINGS ----
export async function getUiStrings() {
  const { data, error } = await supabase
    .from("emilly_ui_strings")
    .select("*");
  if (error) throw error;

  const grouped = {};
  (data || []).forEach(row => {
    if (!grouped[row.context]) grouped[row.context] = {};
    grouped[row.context][row.key] = row.value;
  });
  return grouped;
}

export async function updateUiStrings(context, values) {
  // Delete existing keys for this context
  const { error: delError } = await supabase
    .from("emilly_ui_strings")
    .delete()
    .eq("context", context);
  if (delError) throw delError;

  // Insert new values
  const rows = Object.entries(values).map(([key, value]) => ({ context, key, value }));
  const { data, error } = await supabase
    .from("emilly_ui_strings")
    .insert(rows)
    .select();
  if (error) throw error;
  return data;
}

// ---- PROFILE ----
export async function getProfile(userId) {
  const { data, error } = await supabase
    .from("emilly_profiles")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) return null;
  return data;
}

export async function updateProfile(userId, updates) {
  const { data, error } = await supabase
    .from("emilly_profiles")
    .upsert({ user_id: userId, ...updates }, { onConflict: 'user_id' })
    .select();
  if (error) throw error;
  return data ? data[0] : null;
}

export async function updateUser(userId, updates) {
  const { data, error } = await supabase
    .from("emilly_users")
    .update(updates)
    .eq("id", userId)
    .select();
  if (error) throw error;
  return data[0];
}

export async function uploadAvatar(file, userId) {
  const ext = file.name.split(".").pop();
  const path = `avatars/${userId}.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from("avatars")
    .upload(path, file, { upsert: true });
  if (uploadError) throw uploadError;
  const { data: { publicUrl } } = supabase.storage
    .from("avatars")
    .getPublicUrl(path);
  return publicUrl;
}

export async function uploadSiteImage(file, folder = "general") {
  const timestamp = Date.now();
  const ext = file.name.split(".").pop();
  const path = `${folder}/${timestamp}_${Math.random().toString(36).substring(7)}.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from("site")
    .upload(path, file, { upsert: true });
  if (uploadError) throw uploadError;
  const { data: { publicUrl } } = supabase.storage
    .from("site")
    .getPublicUrl(path);
  return publicUrl;
}

// ---- FAQS ----
export async function getFAQs() {
  const { data, error } = await supabase
    .from("emilly_faqs")
    .select("*")
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createFAQ(form) {
  const { data, error } = await supabase
    .from("emilly_faqs")
    .insert(form)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateFAQ(id, updates) {
  const { data, error } = await supabase
    .from("emilly_faqs")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteFAQ(id) {
  const { error } = await supabase.from("emilly_faqs").delete().eq("id", id);
  if (error) throw error;
}

// ---- FAQ Questions ----
export async function createFaqQuestion({ name, email, question }) {
  const res = await fetch("/api/faq-question", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, question }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Erro ao enviar pergunta");
  }
  return res.json();
}

export async function getFaqQuestions() {
  const { data, error } = await supabase
    .from("emilly_faq_questions")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateFaqQuestion(id, updates) {
  const { data, error } = await supabase
    .from("emilly_faq_questions")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteFaqQuestion(id) {
  const { error } = await supabase.from("emilly_faq_questions").delete().eq("id", id);
  if (error) throw error;
}

export async function answerFaqQuestion({ id, answer }) {
  const res = await fetch("/api/faq-answer", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, answer }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Erro ao responder pergunta");
  }
  return res.json();
}

export async function changePassword(userId, currentPassword, newPassword) {
  const res = await fetch("/api/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, currentPassword, newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Falha ao alterar senha");
}
